import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Clock, Eye, Tag, ArrowLeft, ChevronRight, Brain, Moon, Shield, Zap, Sparkles, Heart, BookOpen, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_META = {
  uyku: { label: 'Uyku', icon: Moon, color: '#22D3EE' },
  dikkat: { label: 'Dikkat', icon: Brain, color: '#A78BFA' },
  kaygi: { label: 'Kaygı', icon: Shield, color: '#F59E0B' },
  stres: { label: 'Stres', icon: Zap, color: '#EF4444' },
  enerji: { label: 'Enerji', icon: Sparkles, color: '#FBBF24' },
  hafiza: { label: 'Hafıza', icon: Brain, color: '#34D399' },
  genel: { label: 'Genel', icon: Heart, color: '#60A5FA' },
};

/* Simple markdown renderer */
const renderMarkdown = (md) => {
  if (!md) return null;
  const lines = md.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('### ')) {
      elements.push(<h3 key={i} className="text-lg font-bold mt-8 mb-3" style={{ color: '#0F172A' }}>{line.slice(4)}</h3>);
    } else if (line.startsWith('## ')) {
      elements.push(<h2 key={i} className="text-xl font-bold mt-10 mb-4" style={{ color: '#0F172A' }}>{line.slice(3)}</h2>);
    } else if (line.startsWith('- ') || line.startsWith('* ')) {
      const items = [];
      while (i < lines.length && (lines[i].startsWith('- ') || lines[i].startsWith('* '))) {
        items.push(<li key={`li-${i}`} className="mb-1.5" style={{ color: '#475569' }}>{processInline(lines[i].slice(2))}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="list-disc pl-6 mb-4 space-y-1" style={{ color: '#475569' }}>{items}</ul>);
      continue;
    } else if (/^\d+\.\s/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s/.test(lines[i])) {
        items.push(<li key={`oli-${i}`} className="mb-1.5" style={{ color: '#475569' }}>{processInline(lines[i].replace(/^\d+\.\s/, ''))}</li>);
        i++;
      }
      elements.push(<ol key={`ol-${i}`} className="list-decimal pl-6 mb-4 space-y-1" style={{ color: '#475569' }}>{items}</ol>);
      continue;
    } else if (line.startsWith('> ')) {
      elements.push(
        <blockquote key={i} className="pl-4 py-2 my-4 rounded-r-xl" style={{ borderLeft: '3px solid #3B82F6', background: 'rgba(59,130,246,0.04)', color: '#475569', fontStyle: 'italic' }}>
          {processInline(line.slice(2))}
        </blockquote>
      );
    } else if (line.trim() === '') {
      // skip empty
    } else {
      elements.push(<p key={i} className="mb-4 leading-relaxed" style={{ color: '#475569', fontSize: '15.5px', lineHeight: '1.8' }}>{processInline(line)}</p>);
    }
    i++;
  }
  return elements;
};

const processInline = (text) => {
  if (!text) return text;
  const parts = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch) {
      const idx = remaining.indexOf(boldMatch[0]);
      if (idx > 0) parts.push(<span key={key++}>{remaining.slice(0, idx)}</span>);
      parts.push(<strong key={key++} style={{ color: '#0F172A', fontWeight: 600 }}>{boldMatch[1]}</strong>);
      remaining = remaining.slice(idx + boldMatch[0].length);
    } else {
      parts.push(<span key={key++}>{remaining}</span>);
      break;
    }
  }
  return parts;
};

const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);

  useEffect(() => {
    const loadPost = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/blog/posts/${slug}`);
        setPost(data);
        // SEO: Update document title and meta
        document.title = `${data.title} | Doktor Cebinde Blog`;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', data.meta_description || '');
        else {
          const m = document.createElement('meta');
          m.name = 'description';
          m.content = data.meta_description || '';
          document.head.appendChild(m);
        }
        // Load related posts from same category
        const { data: related } = await axios.get(`${BACKEND_URL}/api/blog/posts?category=${data.category}&limit=3`);
        setRelatedPosts((related.posts || []).filter(p => p.slug !== slug).slice(0, 2));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    loadPost();
    window.scrollTo(0, 0);
    return () => { document.title = 'Doktor Cebinde'; };
  }, [slug]);

  const catMeta = CATEGORY_META[post?.category] || CATEGORY_META.genel;
  const CatIcon = catMeta.icon;

  if (loading) {
    return (
      <div className="landing-page min-h-screen" style={{ background: 'linear-gradient(180deg, #F7FAFE 0%, #EEF5FC 100%)' }}>
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader className="w-6 h-6 animate-spin" style={{ color: '#3B82F6' }} />
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="landing-page min-h-screen" style={{ background: 'linear-gradient(180deg, #F7FAFE 0%, #EEF5FC 100%)' }}>
        <Header />
        <div className="text-center py-32">
          <BookOpen className="w-12 h-12 mx-auto mb-4" style={{ color: '#CBD5E1' }} />
          <p style={{ color: '#64748B', fontSize: '16px' }}>Yazi bulunamadi.</p>
          <Link to="/blog" className="inline-block mt-4 text-sm font-medium" style={{ color: '#3B82F6' }}>Blog'a dön</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="landing-page min-h-screen" style={{ background: 'linear-gradient(180deg, #F7FAFE 0%, #EEF5FC 100%)' }}>
      <Header />

      {/* Breadcrumb */}
      <div className="max-w-3xl mx-auto px-4 pt-6">
        <div className="flex items-center gap-2 text-sm" style={{ color: '#94A3B8' }}>
          <Link to="/blog" className="hover:text-blue-500 transition flex items-center gap-1" data-testid="blog-breadcrumb-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Blog
          </Link>
          <ChevronRight className="w-3 h-3" />
          <span style={{ color: catMeta.color }}>{catMeta.label}</span>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-4 pb-16">
        {/* Header */}
        <header className="mt-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold" style={{ background: `${catMeta.color}15`, color: catMeta.color }}>
              <CatIcon className="w-3.5 h-3.5" /> {catMeta.label}
            </span>
            <span className="flex items-center gap-1 text-xs" style={{ color: '#94A3B8' }}>
              <Clock className="w-3 h-3" /> {post.reading_time_min || 4} dk okuma
            </span>
            <span className="flex items-center gap-1 text-xs" style={{ color: '#94A3B8' }}>
              <Eye className="w-3 h-3" /> {post.view_count} görüntülenme
            </span>
          </div>

          <h1 style={{ fontSize: '32px', fontWeight: 800, color: '#0F172A', lineHeight: 1.25 }} data-testid="blog-post-title">
            {post.title}
          </h1>

          <p className="mt-3" style={{ color: '#64748B', fontSize: '16px', lineHeight: 1.6 }} data-testid="blog-post-description">
            {post.meta_description}
          </p>

          {/* Author & Date */}
          <div className="flex items-center gap-3 mt-5 pt-5" style={{ borderTop: '1px solid rgba(15,23,42,0.06)' }}>
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #3B82F6, #A78BFA)' }}>
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold" style={{ color: '#0F172A' }}>{post.author || 'Doktor Cebinde'}</p>
              <p className="text-xs" style={{ color: '#94A3B8' }}>
                {new Date(post.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        </header>

        {/* Featured Image */}
        {post.image_url && (
          <div className="rounded-2xl overflow-hidden mb-8" style={{ boxShadow: '0 8px 30px rgba(15,23,42,0.08)' }}>
            <img src={post.image_url} alt={post.title} className="w-full h-auto" data-testid="blog-post-image" />
          </div>
        )}

        {/* Content */}
        <div className="rounded-2xl p-6 sm:p-8" style={{
          background: 'rgba(255,255,255,0.85)',
          border: '1px solid rgba(15,23,42,0.05)',
          boxShadow: '0 4px 20px rgba(15,23,42,0.06)',
        }} data-testid="blog-post-content">
          {renderMarkdown(post.content)}
        </div>

        {/* CTA */}
        {post.cta_text && (
          <div className="mt-8 rounded-2xl p-6 text-center" style={{
            background: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(167,139,250,0.08))',
            border: '1px solid rgba(59,130,246,0.12)',
          }} data-testid="blog-post-cta">
            <Brain className="w-10 h-10 mx-auto mb-3" style={{ color: '#3B82F6' }} />
            <p className="font-semibold mb-2" style={{ color: '#0F172A', fontSize: '16px' }}>{post.cta_text}</p>
            <Link
              to="/register"
              className="inline-block mt-3 px-8 py-3 rounded-2xl text-white font-semibold transition-all hover:-translate-y-0.5"
              style={{ background: 'linear-gradient(135deg, #3B82F6, #2563EB)', boxShadow: '0 8px 24px rgba(59,130,246,0.3)', fontSize: '15px' }}
              data-testid="blog-cta-register"
            >
              Ücretsiz Başla
            </Link>
          </div>
        )}

        {/* Keywords */}
        {post.keywords?.length > 0 && (
          <div className="mt-6 flex flex-wrap gap-2" data-testid="blog-post-keywords">
            {post.keywords.map((kw, i) => (
              <span key={i} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs" style={{ background: 'rgba(15,23,42,0.04)', color: '#64748B' }}>
                <Tag className="w-3 h-3" /> {kw}
              </span>
            ))}
          </div>
        )}

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <div className="mt-12" data-testid="related-posts">
            <h3 className="font-bold mb-4" style={{ color: '#0F172A', fontSize: '18px' }}>Benzer Yazilar</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {relatedPosts.map(rp => (
                <Link
                  key={rp.post_id}
                  to={`/blog/${rp.slug}`}
                  className="group block rounded-xl p-4 transition-all hover:-translate-y-0.5"
                  style={{ background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(15,23,42,0.05)', boxShadow: '0 2px 10px rgba(15,23,42,0.04)' }}
                >
                  <h4 className="font-semibold mb-1.5 group-hover:text-blue-600 transition-colors line-clamp-2" style={{ color: '#0F172A', fontSize: '14px' }}>
                    {rp.title}
                  </h4>
                  <p className="text-xs line-clamp-2" style={{ color: '#94A3B8' }}>{rp.meta_description}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </article>

      <Footer />
    </div>
  );
};

export default BlogPostPage;

